import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { QueryScope } from "@/queries/scope";

/**
 * The client and scope a hint invalidates under. The listener hands it to a
 * row only when both exist: a client, and a signed-in principal to key under.
 */
export interface TCached {
  readonly client: QueryClient;
  readonly scope: QueryScope;
}

/**
 * What a hint does to one covered read, in two steps.
 *
 * - `mark` runs as the hint arrives: the cached copy is marked stale or
 *   dropped, so the next read asks the server. It sends no request.
 * - `reread` refreshes what is on screen. The listener runs it after a random
 *   delay, once for a burst of the same hint, and only while the tab is
 *   visible: a hint that lands on a hidden tab waits for it to be visible
 *   again (NG-2686), since an open picker there would otherwise keep the old
 *   list.
 *
 * `flag` is the switch the domain's hooks take (`useScopedQuery`): a
 * domain's kill switch, or omitted or `null` once the domain reads only
 * through Query. The row runs only while its switch is on.
 *
 * `THost` is the part of the root store the row reaches, spelled out per
 * domain rather than imported: the root store registers the listener, and an
 * import back would be a cycle.
 */
export interface THintTarget<THost = unknown> {
  readonly flag?: string | null;
  mark(root: THost, workspaceSlug: string, cache: TCached): void;
  reread(root: THost, workspaceSlug: string, cache: TCached): void;
}

/** One domain's rows, keyed `domain:scope` as the backend names them. */
export type THintTargets<THost = unknown> = Readonly<Record<string, THintTarget<THost>>>;

/**
 * The row for a read components observe through `useQuery` (contract §9, a
 * hook), under every key starting `keyOf(scope)`. `mark` invalidates with
 * `refetchType: "none"`, and `reread` refetches the active, stale ones, so
 * observers re-read under the listener's timing rather than all at once.
 * `invalidateScoped` would refetch at once, so it is not used here. A query
 * nobody observes is only marked: its next read asks the server.
 */
export function observedRead(
  keyOf: (scope: QueryScope) => QueryKey,
  { flag }: { readonly flag?: string | null } = {},
): THintTarget {
  return {
    ...(flag === undefined ? {} : { flag }),
    mark: (_root, _slug, { client, scope }) =>
      void client.invalidateQueries({ queryKey: keyOf(scope), refetchType: "none" }),
    reread: (_root, _slug, { client, scope }) =>
      void client.refetchQueries({ queryKey: keyOf(scope), type: "active", stale: true }),
  };
}
